import React, { useEffect, useState } from 'react';
import './App.css';
import { translations } from './translations';

const App = () => {
  const [scrollY, setScrollY] = useState(0);
  const [navBackground, setNavBackground] = useState(false);
  const [language, setLanguage] = useState('ko');
  const [isLanguageDetected, setIsLanguageDetected] = useState(false);

  // Get translation text
  const t = translations[language];

  // Detect language based on IP
  useEffect(() => {
    const detectLanguage = async () => {
      // Check if language is already set in localStorage
      const savedLanguage = localStorage.getItem('seesoLanguage');
      if (savedLanguage) {
        setLanguage(savedLanguage);
        setIsLanguageDetected(true);
        return;
      }

      try {
        // Detect country by IP using ipapi.co
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        const countryCode = data.country_code;

        let detectedLanguage = 'en'; // Default to English

        if (countryCode === 'JP') {
          detectedLanguage = 'ja';
        } else if (countryCode === 'KR') {
          detectedLanguage = 'ko';
        }

        setLanguage(detectedLanguage);
        localStorage.setItem('seesoLanguage', detectedLanguage);
      } catch (error) {
        // If IP detection fails, use browser language as fallback
        const browserLang = navigator.language.toLowerCase();
        let fallbackLanguage = 'en';

        if (browserLang.startsWith('ja')) {
          fallbackLanguage = 'ja';
        } else if (browserLang.startsWith('ko')) {
          fallbackLanguage = 'ko';
        }

        setLanguage(fallbackLanguage);
        localStorage.setItem('seesoLanguage', fallbackLanguage);
      } finally {
        setIsLanguageDetected(true);
      }
    };

    detectLanguage();
  }, []);

  // Handle language change
  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('seesoLanguage', lang);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.pageYOffset);
      setNavBackground(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Only run after language is detected and component is mounted
    if (!isLanguageDetected) return;

    // Scroll reveal animation
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, observerOptions);

    // Use requestAnimationFrame to ensure DOM is fully rendered
    const rafId = requestAnimationFrame(() => {
      // Observe all scroll-reveal elements
      const elements = document.querySelectorAll('.scroll-reveal');
      elements.forEach(el => {
        // Check if element is already in viewport
        const rect = el.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

        if (isInViewport) {
          // If already in viewport, reveal immediately
          el.classList.add('revealed');
        }

        // Observe for future scroll events
        observer.observe(el);
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [language, isLanguageDetected]); // Re-run when language changes or is detected

  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();
    const target = document.querySelector(targetId);
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const heroOpacity = Math.max(1 - scrollY / window.innerHeight, 0);
  const heroTransform = `translateY(${scrollY * -0.5}px)`;

  // Don't render until language is detected
  if (!isLanguageDetected) {
    return null;
  }

  return (
    <div className="bg-gray-50 text-gray-800">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full backdrop-blur-md z-50 border-b border-gray-100 transition-colors ${navBackground ? 'bg-white/90' : 'bg-white/80'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="text-xl sm:text-2xl font-bold logo-text lowercase">seeso</div>
            <div className="hidden lg:flex space-x-8">
              <a href="#vision" onClick={(e) => handleSmoothScroll(e, '#vision')} className="text-gray-600 hover:text-gray-900 transition-colors">{t.nav.vision}</a>
              <a href="#services" onClick={(e) => handleSmoothScroll(e, '#services')} className="text-gray-600 hover:text-gray-900 transition-colors">{t.nav.services}</a>
              <a href="#journey" onClick={(e) => handleSmoothScroll(e, '#journey')} className="text-gray-600 hover:text-gray-900 transition-colors">{t.nav.journey}</a>
              <a href="#contact" onClick={(e) => handleSmoothScroll(e, '#contact')} className="text-gray-600 hover:text-gray-900 transition-colors">{t.nav.contact}</a>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <a href="https://second-team.com/" target="_blank" rel="noopener noreferrer" className="hidden sm:inline-block btn-secondary text-xs sm:text-sm px-3 sm:px-6 py-2 sm:py-3">{t.nav.secondTeam}</a>
              <a href="https://careerly.co.kr/" target="_blank" rel="noopener noreferrer" className="btn-primary text-xs sm:text-sm px-4 sm:px-6 py-2 sm:py-3">{t.nav.careerly}</a>

              {/* Language Selector */}
              <div className="flex items-center space-x-1 ml-2">
                <button
                  onClick={() => changeLanguage('en')}
                  className={`text-xs sm:text-sm px-2 py-1 font-semibold transition-colors ${
                    language === 'en' ? 'text-black underline' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  EN
                </button>
                <span className="text-gray-400">/</span>
                <button
                  onClick={() => changeLanguage('ja')}
                  className={`text-xs sm:text-sm px-2 py-1 font-semibold transition-colors ${
                    language === 'ja' ? 'text-black underline' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  JP
                </button>
                <span className="text-gray-400">/</span>
                <button
                  onClick={() => changeLanguage('ko')}
                  className={`text-xs sm:text-sm px-2 py-1 font-semibold transition-colors ${
                    language === 'ko' ? 'text-black underline' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  KR
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        id="hero"
        className="hero-fade min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20"
        style={{ opacity: heroOpacity, transform: heroTransform }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <div className="floating-animation mb-6 sm:mb-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto lemon-gradient rounded-2xl flex items-center justify-center mb-6 sm:mb-8 pulse-animation">
              <i className="fas fa-rocket text-black text-xl sm:text-2xl"></i>
            </div>
          </div>

          <h1 className="text-3xl sm:text-5xl md:text-7xl mb-6 sm:mb-8 leading-relaxed">
            <span className="text-gray-600 font-normal text-2xl sm:text-4xl md:text-5xl block mb-2">{t.hero.subtitle}</span>
            <span className="text-black font-bold text-3xl sm:text-5xl md:text-7xl block mb-2">{t.hero.title1} <span className="inline-block" style={{textDecoration: 'underline', textDecorationColor: 'rgb(229, 255, 30)', textDecorationThickness: '3px'}}></span></span>
            <span className="text-gray-900 font-bold text-3xl sm:text-5xl md:text-7xl block">{t.hero.title2}</span>
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl text-gray-700 mb-8 sm:mb-12 max-w-3xl mx-auto font-normal px-4">
            {t.hero.description} <span className="text-black font-bold">{t.hero.company}</span>
          </p>

          <div className="flex justify-center">
            <a href="#vision" onClick={(e) => handleSmoothScroll(e, '#vision')} className="btn-primary text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4">{t.hero.cta}</a>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section id="vision" className="section-spacing bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="scroll-reveal text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-light mb-8">
              <span className="text-black font-bold">{t.vision.title}</span>
            </h2>
            <div className="w-20 h-1 lemon-bg mx-auto mb-12"></div>
          </div>

          <div className="scroll-reveal max-w-4xl mx-auto text-center">
            <p className="text-2xl md:text-3xl text-gray-800 mb-8 font-medium">
              {t.vision.question1}
            </p>
            <p className="text-3xl md:text-4xl text-black font-bold mb-12">
              {t.vision.question2}
            </p>

            <div className="bg-gray-50 rounded-3xl p-12 mb-16">
              <p className="text-lg md:text-xl text-gray-800 leading-relaxed font-medium">
                {t.vision.description1} <strong className="text-black">{t.vision.description1Bold}</strong>{t.vision.description2}<br/>
                {t.vision.description3}<br/>
                {t.vision.description4} <strong className="text-black">{t.vision.description4Bold}</strong>{t.vision.description5}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-spacing bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="scroll-reveal text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-light mb-8">
              <span className="text-black font-bold">{t.services.title}</span> {t.services.subtitle}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* 세컨팀 */}
            <div className="scroll-reveal card-hover bg-white rounded-3xl p-6 sm:p-8 lg:p-12 shadow-lg">
              <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mb-8 lemon-border border-2">
                <i className="fas fa-globe lemon-text text-2xl"></i>
              </div>

              <h3 className="text-2xl md:text-3xl font-medium mb-6">
                <span className="text-black font-bold">{t.services.secondTeam.title}</span> {t.services.secondTeam.subtitle}
              </h3>

              <div className="space-y-6 text-gray-700">
                <p className="text-lg font-medium">
                  {t.services.secondTeam.intro1}<br/>
                  {t.services.secondTeam.intro2} <strong className="font-bold">{t.services.secondTeam.intro2Bold}</strong>
                </p>

                <p className="font-medium">
                  {t.services.secondTeam.description1}<br/>
                  {t.services.secondTeam.description2}<br/>
                  {t.services.secondTeam.description3}<br/>
                  <strong className="font-bold">{t.services.secondTeam.description4}</strong>{t.services.secondTeam.description5}
                </p>

                <div className="lemon-bg-soft p-6 rounded-2xl border lemon-border">
                  <p className="text-gray-800 font-semibold">
                    {t.services.secondTeam.highlight1}<br/>
                    {t.services.secondTeam.highlight2} <strong className="text-xl font-bold" style={{textDecoration: 'underline', textDecorationColor: 'rgb(229, 255, 30)', textDecorationThickness: '3px'}}>{t.services.secondTeam.highlight2Bold}</strong>{t.services.secondTeam.highlight3}
                  </p>
                </div>
              </div>

              <a href="https://second-team.com/" target="_blank" rel="noopener noreferrer" className="btn-primary inline-block mt-8">{t.services.secondTeam.cta}</a>
            </div>

            {/* 커리어리 */}
            <div className="scroll-reveal card-hover bg-white rounded-3xl p-6 sm:p-8 lg:p-12 shadow-lg">
              <div className="w-16 h-16 lemon-gradient rounded-2xl flex items-center justify-center mb-8">
                <i className="fas fa-robot text-black text-2xl"></i>
              </div>

              <h3 className="text-2xl md:text-3xl font-medium mb-6">
                <span className="text-black font-bold">{t.services.careerly.title}</span> {t.services.careerly.subtitle}
              </h3>

              <div className="space-y-6 text-gray-700">
                <p className="text-lg font-medium">
                  {t.services.careerly.intro1}<br/>
                  {t.services.careerly.intro2}
                </p>

                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-black rounded-full"></div>
                    <p className="font-medium">{t.services.careerly.point1}</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-gray-700 rounded-full"></div>
                    <p className="font-medium">{t.services.careerly.point2}</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                    <p className="font-medium">{t.services.careerly.point3}</p>
                  </div>
                </div>

                <div className="bg-gray-900 p-6 rounded-2xl">
                  <p className="text-white font-semibold">
                    {t.services.careerly.highlight1}<br/>
                    <strong className="lemon-text">{t.services.careerly.highlight2}</strong>{t.services.careerly.highlight3}
                  </p>
                </div>
              </div>

              <a href="https://careerly.co.kr/" target="_blank" rel="noopener noreferrer" className="btn-primary inline-block mt-8">{t.services.careerly.cta}</a>
            </div>
          </div>
        </div>
      </section>

      {/* Connection Vision */}
      <section className="section-spacing bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="scroll-reveal">
            <h2 className="text-4xl md:text-5xl font-light mb-16 text-center">
              <span className="text-black font-bold">{t.connection.title}</span>
            </h2>

            <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="space-y-6 slide-in-left order-2 md:order-1">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {t.connection.subtitle}
                </h3>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  {t.connection.description1}<br/>
                  {t.connection.description2}
                </p>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  {t.connection.description3}<br/>
                  <strong className="text-black text-lg sm:text-xl block mt-4">{t.connection.description4}</strong>
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-6 sm:p-8 lg:p-10 border border-gray-200 slide-in-right order-1 md:order-2">
                <h4 className="text-xl font-bold mb-8 text-black">{t.connection.promises.title}</h4>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-black rounded-full flex-shrink-0 flex items-center justify-center icon-hover">
                      <i className="fas fa-lightbulb text-white text-sm"></i>
                    </div>
                    <div>
                      <h5 className="font-bold mb-2 text-gray-900">{t.connection.promises.promise1Title}</h5>
                      <p className="text-gray-600">{t.connection.promises.promise1Desc}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-black rounded-full flex-shrink-0 flex items-center justify-center icon-hover">
                      <i className="fas fa-robot text-white text-sm"></i>
                    </div>
                    <div>
                      <h5 className="font-bold mb-2 text-gray-900">{t.connection.promises.promise2Title}</h5>
                      <p className="text-gray-600">{t.connection.promises.promise2Desc}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-black rounded-full flex-shrink-0 flex items-center justify-center icon-hover">
                      <i className="fas fa-trophy text-white text-sm"></i>
                    </div>
                    <div>
                      <h5 className="font-bold mb-2 text-gray-900">{t.connection.promises.promise3Title}</h5>
                      <p className="text-gray-600">{t.connection.promises.promise3Desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section id="journey" className="section-spacing bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="scroll-reveal text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-light mb-8">
              <span className="text-black font-bold">{t.journey.title}</span> {t.journey.subtitle}
            </h2>
            <p className="text-xl text-gray-700 font-medium max-w-3xl mx-auto">
              {t.journey.description1}<br/>
              {t.journey.description2}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-12">
              {/* 2016-2020 */}
              <div className="scroll-reveal timeline-item">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center mb-4">
                    <span className="lemon-text font-bold text-lg">{t.journey.period1.year}</span>
                    <span className="ml-4 text-2xl">🚀</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-black">{t.journey.period1.title}</h3>
                  <p className="text-gray-800 font-medium mb-4">
                    {t.journey.period1.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {t.journey.period1.tags.map((tag, index) => (
                      <span key={index} className="px-3 py-1 lemon-bg-soft text-gray-900 rounded-full text-sm font-medium">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* 2021-2023 */}
              <div className="scroll-reveal timeline-item">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center mb-4">
                    <span className="text-black font-bold text-lg">{t.journey.period2.year}</span>
                    <span className="ml-4 text-2xl">🧩</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-black">{t.journey.period2.title}</h3>
                  <p className="text-gray-800 font-medium mb-4">
                    {t.journey.period2.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {t.journey.period2.tags.map((tag, index) => (
                      <span key={index} className="px-3 py-1 bg-gray-200 text-gray-800 rounded-full text-sm font-medium">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* 2024~ */}
              <div className="scroll-reveal timeline-item">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center mb-4">
                    <span className="lemon-text font-bold text-lg">{t.journey.period3.year}</span>
                    <span className="ml-4 text-2xl">🔮</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-black">{t.journey.period3.title}</h3>
                  <p className="text-gray-800 font-medium mb-4">
                    {t.journey.period3.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {t.journey.period3.tags.map((tag, index) => (
                      <span key={index} className="px-3 py-1 lemon-bg-soft text-gray-900 rounded-full text-sm font-medium">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Join Us Section */}
      <section id="contact" className="section-spacing bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="scroll-reveal text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light mb-8">
              <span className="text-black font-bold">{t.joinUs.title}</span>
            </h2>
            <p className="text-xl text-gray-700 font-medium">{t.joinUs.description}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            <div className="scroll-reveal card-hover bg-white rounded-2xl p-6 sm:p-8 text-center shadow-lg fade-in-up">
              <div className="w-16 h-16 lemon-gradient rounded-2xl flex items-center justify-center mx-auto mb-6 icon-hover">
                <i className="fas fa-code text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 text-black">{t.joinUs.roles.developer.title}</h3>
              <p className="text-gray-700 font-medium whitespace-pre-line">{t.joinUs.roles.developer.description}</p>
            </div>

            <div className="scroll-reveal card-hover bg-white rounded-2xl p-6 sm:p-8 text-center shadow-lg fade-in-up" style={{animationDelay: '0.1s'}}>
              <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6 icon-hover">
                <i className="fas fa-rocket lemon-text text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 text-black">{t.joinUs.roles.startup.title}</h3>
              <p className="text-gray-700 font-medium whitespace-pre-line">{t.joinUs.roles.startup.description}</p>
            </div>

            <div className="scroll-reveal card-hover bg-white rounded-2xl p-6 sm:p-8 text-center shadow-lg fade-in-up" style={{animationDelay: '0.2s'}}>
              <div className="w-16 h-16 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-6 lemon-border border-2 icon-hover">
                <i className="fas fa-briefcase lemon-text text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 text-black">{t.joinUs.roles.partnership.title}</h3>
              <p className="text-gray-700 font-medium whitespace-pre-line">{t.joinUs.roles.partnership.description}</p>
            </div>

            <div className="scroll-reveal card-hover bg-white rounded-2xl p-6 sm:p-8 text-center shadow-lg fade-in-up" style={{animationDelay: '0.3s'}}>
              <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6 icon-hover">
                <i className="fas fa-chart-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4 text-black">{t.joinUs.roles.investor.title}</h3>
              <p className="text-gray-700 font-medium whitespace-pre-line">{t.joinUs.roles.investor.description}</p>
            </div>
          </div>

          <div className="text-center mt-12 sm:mt-16">
            <a href="mailto:partner@seeso.kr" className="btn-primary text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4">{t.joinUs.cta}</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-12">
            <div className="text-3xl font-bold lemon-text mb-6">seeso</div>
            <p className="text-lg text-gray-400 mb-8">
              {t.footer.tagline}
            </p>
          </div>

          <div className="border-t border-gray-700 pt-8 space-y-3 text-sm">
            <p className="font-medium text-gray-300">{t.footer.company}</p>
            <p>{t.footer.address1}</p>
            <p>{t.footer.businessNumber}</p>
            <p>{t.footer.privacy}</p>
            <p className="text-gray-400 mt-6">{t.footer.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
